import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const logoSrc = `${process.env.PUBLIC_URL}/assets/chakracue-logo.jpeg`;

export default function LoginPage() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [managerPin, setManagerPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login({ username, password });
      navigate(from, { replace: true });
    } catch (err) {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const handleManagerLogin = async (event) => {
    event.preventDefault();
    setError("");
    if (!managerPin.trim()) {
      setError("Enter the manager PIN");
      return;
    }
    setLoading(true);

    try {
      await login({ role: "Manager", password: managerPin });
      navigate(from, { replace: true });
    } catch (err) {
      setError("Incorrect manager PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 flex items-start sm:items-center justify-center px-4 py-6 overflow-y-auto">
      <div className="w-full max-w-md rounded-3xl border border-zinc-800 bg-[#111113]/95 p-5 sm:p-8 shadow-2xl shadow-black/20">
        <div className="mb-6 sm:mb-8 text-center">
          <img
            src={logoSrc}
            alt="ChakraCue logo"
            className="mx-auto mb-4 sm:mb-5 h-16 w-16 sm:h-28 sm:w-28 rounded-2xl border border-amber-500/30 object-cover shadow-xl shadow-black/40"
          />
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">ChakraCue Login</h1>
          <p className="mt-2 text-zinc-400 text-xs sm:text-sm">
            Sign in with the manager PIN, or use admin credentials for full access.
          </p>
        </div>

        <form onSubmit={handleManagerLogin} className="rounded-2xl border border-zinc-700 bg-green-600/10 p-4 mb-4" data-testid="manager-login">
          <div className="mb-1 font-semibold text-zinc-100">Manager sign in</div>
          <p className="mb-3 text-sm text-zinc-400">
            Run tables and take cafe orders. Ask the owner for the PIN.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              inputMode="numeric"
              value={managerPin}
              onChange={(e) => setManagerPin(e.target.value)}
              className="flex-1 rounded-2xl border border-zinc-700 bg-[#09090b] px-4 py-3 text-zinc-100 outline-none focus:border-green-500"
              placeholder="Manager PIN"
              autoComplete="current-password"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto rounded-2xl bg-green-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-60 shrink-0"
            >
              {loading ? "..." : "Enter"}
            </button>
          </div>
        </form>

        <div className="border-t border-zinc-800 pt-4 mt-4">
          <div className="mb-4 text-sm text-zinc-400">Admin login</div>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Username</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded-2xl border border-zinc-700 bg-[#09090b] px-4 py-3 text-zinc-100 outline-none focus:border-green-500"
                placeholder="Admin username"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-zinc-700 bg-[#09090b] px-4 py-3 text-zinc-100 outline-none focus:border-green-500"
                placeholder="Password"
                autoComplete="current-password"
              />
            </div>

            {error && <p className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-zinc-800 px-4 py-3 text-sm font-semibold text-white transition hover:bg-zinc-700 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Admin sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
